/* ---------------------------------------------
 * Core enums (match NemoAPI.Enums.Themis)
 * --------------------------------------------- */

import { DropdownOptions } from "../../../hooks/useAwardableAwards";

export enum ThemisConditionValueType {
  String = "String",
  Number = "Number",
  Boolean = "Boolean",

  // Stored/compared as Unix UTC milliseconds (long)
  DateTimeUtcMs = "DateTimeUtcMs",

  // Engine treats these as numerics but distinguishes for parsing/operators.
  Int = "Int",
  Decimal = "Decimal",
}

export enum ThemisConditionOperator {
  Equals = "Equals",
  NotEquals = "NotEquals",

  In = "In",
  NotIn = "NotIn",

  GreaterThan = "GreaterThan",
  GreaterThanOrEqual = "GreaterThanOrEqual",
  LessThan = "LessThan",
  LessThanOrEqual = "LessThanOrEqual",
  Between = "Between",

  Contains = "Contains",
  StartsWith = "StartsWith",
  EndsWith = "EndsWith",

  IsNull = "IsNull",
  IsNotNull = "IsNotNull",
}

export enum ThemisEffectOperation {
  Addition = "Addition",
  Subtraction = "Subtraction",
  Multiplication = "Multiplication",
  Set = "Set",
  Limit = "Limit",
  Cap = "Cap",
}

/**
 * IMPORTANT:
 * The backend uses ThemisPricingPhase keys (ThemisPhaseKey string wrapper).
 * These names must match server-side constants, not older UI names.
 *
 * Based on the engine code:
 * - Line phases: ResolveBasePrice, ApplyLineDiscounts, ApplyLineFees, ApplyLineTaxes
 * - Target phases: ApplyTargetDiscounts, ApplyTargetFees, ApplyTargetTaxes
 * - Final: FinalizeTotals (and potentially other non-math phases later)
 */
export enum ThemisPhase {
  ResolveBasePrice = "pricing.resolve_base",
  ApplyLineDiscounts = "pricing.line_discounts",
  ApplyLineFees = "pricing.line_fees",
  ApplyLineTaxes = "pricing.line_taxes",
  ApplyTargetDiscounts = "pricing.target_discounts",
  ApplyTargetFees = "pricing.target_fees",
  ApplyTargetTaxes = "pricing.target_taxes",
  FinalizeTotals = "pricing.finalize",
  Invoicing = "pricing.invoicing",
}

export const ThemisPhaseOptions: DropdownOptions[] = [
  { value: ThemisPhase.ResolveBasePrice, label: "Resolve Base Price" },
  { value: ThemisPhase.ApplyLineDiscounts, label: "Line Discounts" },
  { value: ThemisPhase.ApplyLineFees, label: "Line Fees" },
  { value: ThemisPhase.ApplyLineTaxes, label: "Line Taxes" },
  { value: ThemisPhase.ApplyTargetDiscounts, label: "Target Discounts" },
  { value: ThemisPhase.ApplyTargetFees, label: "Target Fees" },
  { value: ThemisPhase.ApplyTargetTaxes, label: "Target Taxes" },
  { value: ThemisPhase.FinalizeTotals, label: "Finalize Totals" },
  { value: ThemisPhase.Invoicing, label: "Invoicing" },
];

/* ---------------------------------------------
 * Catalog DTOs (Admin field picker)
 * --------------------------------------------- */

/**
 * Catalog returned for field selection UIs.
 * This is the “admin shaped” view, but still grounded in the same keys
 * the engine uses (ScopeKey.FieldName, qualified scopes, etc).
 */
export interface ThemisFieldCatalog {
  scopes: ThemisScope[];
}

export interface ThemisScope {
  /**
   * Scope key. May be base ("Entry") or qualified ("Feature:Paymentz").
   * This key is what must be used when serializing conditions/effects.
   */
  key: string;

  displayName: string;
  sortOrder: number;

  /**
   * Optional UI hints. These are additive and safe if the backend doesn’t send them yet.
   * They help build “smart pickers” without the UI re-deriving everything.
   */
  isSynthetic?: boolean;
  baseScopeKey?: string; // e.g. "Feature"
  qualifier?: string; // e.g. "Paymentz"

  fields: ThemisField[];
}

export interface ThemisField {
  /**
   * Fully-qualified field key that the engine can resolve.
   * Examples:
   * - "Entry.IsCampaign"
   * - "Feature:Paymentz.IsEnabled"
   * - "Entry:LineItem.TotalLineAmount" (qualified scope examples)
   */
  key: string;

  displayName: string;
  sortOrder: number;

  valueType: ThemisConditionValueType;
  allowedOperators: ThemisConditionOperator[];

  /**
   * Optional UI hints
   */
  scopeKey?: string; // duplicate of the scope portion, but explicit for UI convenience
  propertyName?: string; // RHS of field key, e.g. "IsEnabled"
  isVirtual?: boolean; // true if not a reflected CLR property (synthetic/virtual field)
}

/**
 * Reachable catalog is useful when UI is scope-aware (pipeline/root scope selection).
 * The server can return a subset of scopes/fields reachable from a starting scope.
 */
export interface ThemisReachableCatalog {
  targetScopeKey: string;
  scopes: ThemisScope[];
}

/* ---------------------------------------------
 * Ruleset + Rules DTOs
 * --------------------------------------------- */

export interface ThemisRuleSet {
  id: number;
  key: string;
  name: string;
  description?: string | null;
  isEnabled: boolean;
  priority: number;

  /**
   * Membership rows between RuleSet and Rule.
   */
  rules: ThemisRuleSetRule[];
}

export interface ThemisRuleSetRule {
  id: number;
  themisRuleSetId: number;
  themisRuleId: number;

  sortOrder: number;
  isEnabled: boolean;
  overridePriority?: number | null;

  /**
   * Embedded rule.
   */
  rule: ThemisRule;
}

export interface ThemisRule {
  id: number;
  name: string;
  description?: string | null;

  /**
   * Phase key string that must match server-side ThemisPhaseKey.Value.
   */
  phase: ThemisPhase;

  priority: number;
  isEnabled: boolean;
  stopProcessing: boolean;

  conditionGroups: ThemisConditionGroup[];
  effects: ThemisEffect[];
  targetScopeKey: string;
}

export interface ThemisConditionGroup {
  id: number;
  themisRuleId: number;
  name?: string | null;
  sortOrder: number;
  conditions: ThemisCondition[];
}

export interface ThemisCondition {
  id: number;
  themisConditionGroupId: number;

  /**
   * Engine expects: "Scope.Property" OR "QualifiedScope.Property"
   * Examples:
   * - "Entry.IsCampaign"
   * - "Feature:Paymentz.IsEnabled"
   */
  fieldKey: string;

  operator: ThemisConditionOperator;
  valueType: ThemisConditionValueType;
  sortOrder: number;

  // Scalar values (server stores different typed columns)
  stringValue?: string | null;
  boolValue?: boolean | null;

  /**
   * Long/Int:
   * - On server you have IntValue + LongValue (+ LongValue2)
   * - On TS side we just use number and keep both fields so payload matches
   */
  intValue?: number | null;
  longValue?: number | null;

  decimalValue?: number | null;

  // Between support (second value)
  longValue2?: number | null;
  decimalValue2?: number | null;

  /**
   * In/NotIn are stored as JSON strings on the server.
   * Keep these raw so the UI can round-trip exactly,
   * and optionally offer helper parse/serialize functions.
   */
  stringListJson?: string | null;
  numberListJson?: string | null;
}

/* ---------------------------------------------
 * Effects DTOs (discriminated union)
 * --------------------------------------------- */

/**
 * Matches EF discriminator:
 * builder.Entity<ThemisEffect>().HasDiscriminator<string>("EffectType")
 *   .HasValue<ThemisPriceEffect>("Price")
 *   .HasValue<ThemisTimeEffect>("Time")
 *   .HasValue<ThemisDisplayEffect>("Display")
 *   .HasValue<ThemisFieldKeyEffect>("FieldKey");
 */
export type ThemisEffectType = "Price" | "Time" | "Display" | "FieldKey";

/**
 * Common effect fields coming from ThemisEffects table.
 */
export interface ThemisEffectBase {
  id: number;
  themisRuleId: number;

  effectType: ThemisEffectType;

  name?: string | null;
  label?: string | null;
  description?: string | null;

  phase: ThemisPhase;
  operation: ThemisEffectOperation;
}

/**
 * PriceEffect maps to column PriceValue (decimal).
 */
export interface ThemisPriceEffect extends ThemisEffectBase {
  effectType: "Price";
  priceValue: number; // REQUIRED for Price effects
}

/**
 * TimeEffect maps to column TimeValue (likely UTC Unix).
 * Keep as number (ms) unless your UI uses another unit.
 */
export interface ThemisTimeEffect extends ThemisEffectBase {
  effectType: "Time";
  timeValue: number; // REQUIRED for Time effects
}

/**
 * DisplayEffect maps to column DisplayValue (boolean).
 */
export interface ThemisDisplayEffect extends ThemisEffectBase {
  effectType: "Display";
  displayValue: boolean; // REQUIRED for Display effects
}

/**
 * FieldKeyEffect maps to:
 * - FieldKeyValue (nvarchar 300)
 * - FieldKeyValueType (enum)
 * - FieldKeyTarget (enum)
 *
 * Used by engine to resolve a runtime field key and apply it to a pricing target.
 */
export interface ThemisFieldKeyEffect extends ThemisEffectBase {
  effectType: "FieldKey";

  /**
   * The field key to resolve at evaluation-time, e.g. "Entry.LegacyBasePrice"
   */
  fieldKeyValue: string;

  /**
   * How to interpret the resolved field value.
   * Server: ThemisConditionValueType
   */
  fieldKeyValueType: ThemisConditionValueType;

  /**
   * Where to apply the resolved value.
   * Server enum: ThemisFieldKeyEffectTarget
   */
  fieldKeyTarget: ThemisFieldKeyEffectTarget;
}

export type ThemisEffect =
  | ThemisPriceEffect
  | ThemisTimeEffect
  | ThemisDisplayEffect
  | ThemisFieldKeyEffect;

/**
 * Mirrors server enum NemoAPI.Enums.Themis.ThemisFieldKeyEffectTarget
 */
export enum ThemisFieldKeyEffectTarget {
  // Line targets
  LineSubtotal = "LineSubtotal",
  LineDiscountTotal = "LineDiscountTotal",
  LineFeeTotal = "LineFeeTotal",
  LineTaxRate = "LineTaxRate",
  LineTaxAmount = "LineTaxAmount",
  LineEventNow = "LineEventNow",

  // Target (order) targets
  TargetDiscountTotal = "TargetDiscountTotal",
  TargetFeeTotal = "TargetFeeTotal",
  TargetTaxRate = "TargetTaxRate",
  TargetTaxAmount = "TargetTaxAmount",
  TargetEventNow = "TargetEventNow",
}

/* ---------------------------------------------
 * Judgment + breakdown + trace
 * --------------------------------------------- */

export interface ThemisBreakdownLine {
  code: string;
  label: string;
  amount: number;

  // For tax lines
  rate?: number | null;

  sourceRule?: string | null;
  phase?: string | null;

  // null/undefined for target-level breakdown lines
  lineKey?: string | null;
}

export interface ThemisLineJudgment {
  lineKey: string;

  subtotal: number;
  discountTotal: number;
  feeTotal: number;
  taxTotal: number;

  total?: number;

  breakdown: ThemisBreakdownLine[];

  trace: ThemisTrace[];
}

export interface ThemisJudgment {
  subtotal: number;
  discountTotal: number;
  feeTotal: number;
  taxTotal: number;

  total: number;

  lines: ThemisLineJudgment[];

  /**
   * Target-level breakdown lines (fees/discounts/taxes applied at target scope).
   */
  breakdown: ThemisBreakdownLine[];

  /**
   * Target-level trace (rules evaluated at target phases).
   */
  trace: ThemisTrace[];
}

export interface ThemisTrace {
  ruleSetKey?: string | null;
  ruleId: number;
  ruleName: string;

  /**
   * Stored as phase key string on server.
   */
  phase: string;

  matched: boolean;
  stoppedProcessing: boolean;

  conditions: ThemisTraceCondition[];
  effectsApplied: ThemisTraceEffect[];
}

export interface ThemisTraceCondition {
  fieldKey: string;
  operator: string;
  leftValue?: string | null;
  rightValue?: string | null;
  result: boolean;
}

export interface ThemisTraceEffect {
  effectType: string;
  operation: string;
  value?: string | null;
}

/* ---------------------------------------------
 * Small helper(s)
 * --------------------------------------------- */

/**
 * Turns an enum object into UI-friendly options.
 * Works for string-valued enums.
 */
export const enumToOptions = <T extends Record<string, string>>(e: T) =>
  Object.values(e).map((v) => ({ value: v, label: v }));

/* -------------------------------------------------------------------------------------------------
 * Optional helpers (UI convenience)
 * ------------------------------------------------------------------------------------------------- */

/**
 * Extracts "ScopeKey" from a field key ("Scope.Property").
 * For qualified scopes ("Feature:Paymentz.IsEnabled"), returns "Feature:Paymentz".
 */
export const getScopeKeyFromFieldKey = (fieldKey: string): string => {
  const dot = fieldKey.indexOf(".");
  return dot > 0 ? fieldKey.substring(0, dot) : fieldKey;
};

/**
 * Extracts property name from a field key ("Scope.Property").
 */
export const getPropertyNameFromFieldKey = (fieldKey: string): string => {
  const dot = fieldKey.indexOf(".");
  return dot > 0 ? fieldKey.substring(dot + 1) : fieldKey;
};
